import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { useEventLogging } from '../../../models/Logging';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { usePutCustomFieldsMutation } from '../../../queries';
import { Modal } from '../../../web/components/Modal';
import { Input } from '../../../web/components-v2/Input';
import { RoundAddIcon } from '../../../web/components/svgs/icons/RoundAddIcon';
import { XIcon } from '../../../web/components/svgs/icons/XIcon';
import { charmGray } from '../../../web/styles/colors';
import { baseStyleSheet } from '../../../web/styles/styles';
import { styleSheet } from './styles';

const MAX_CUSTOM_FIELDS = 25;

interface INewCustomField {
	id: string;
	name: string;
	label: string;
	isChanged: boolean;
}

const getCustomFields = (initialFields: Api.IFormField<string>[]) => {
	return initialFields.map(field => {
		return {
			id: uuid(),
			name: field.name ?? '',
			label: field.label ?? '',
			isChanged: false,
		};
	});
};

export const CreateCustomFieldsModal = ({
	impersonationContext,
	onSave,
	onCancel,
	initialFields,
}: {
	impersonationContext: Api.IImpersonationContext;
	onSave: () => void;
	onCancel: () => void;
	initialFields?: Api.IFormField<string>[];
}) => {
	const { logApiError } = useEventLogging('CreateCustomFieldsModal');
	const errorMessages = useErrorMessages();

	const [newCustomFields, setNewCustomFields] = React.useState<INewCustomField[]>(
		initialFields?.length
			? getCustomFields(initialFields)
			: [
					{
						id: uuid(),
						name: '',
						label: '',
						isChanged: false,
					},
				]
	);
	const isEditing = initialFields?.length > 0;

	const handleAddMore = () => {
		const newField: INewCustomField = {
			id: uuid(),
			name: '',
			label: '',
			isChanged: false,
		};
		setNewCustomFields([...newCustomFields, newField]);
	};

	const updateCustomField = (customField: INewCustomField, newValue: string) => {
		const updatedFields = [...newCustomFields];
		const currentIndex = newCustomFields.findIndex(newCustomField => customField.id === newCustomField.id);
		updatedFields[currentIndex].label = newValue;
		updatedFields[currentIndex].isChanged = true;
		setNewCustomFields(updatedFields);
	};

	const createCustomFieldsMutation = usePutCustomFieldsMutation({
		impersonationContext,
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			logApiError('SaveCustomFields-Error', error);
		},
	});

	const handleRemoveCustomField = (fieldToRemove: INewCustomField) => {
		const updatedFields = [...newCustomFields];
		const currentIndex = updatedFields.findIndex(newCustomField => fieldToRemove.id === newCustomField.id);
		updatedFields.splice(currentIndex, 1);
		setNewCustomFields(updatedFields);
	};

	const cancel = () => {
		onCancel();
	};

	const getName = (field: INewCustomField) => {
		if (field.name) {
			return field.name;
		}

		// If there are 2 fields that will end up with the same name, append a short number to the end
		const somewhatRandomNumber = new Date().getTime().toString().slice(-4);
		const matches = newCustomFields.filter(existingCustomField => {
			return existingCustomField.label === field.label;
		});

		return matches.length > 1 ? `${field.label}${somewhatRandomNumber}` : field.label;
	};

	const save = async () => {
		await createCustomFieldsMutation.mutateAsync({
			fields: newCustomFields.map(field => {
				return {
					name: getName(field),
					label: field.label,
					fieldType: Api.FormFieldType.String,
				};
			}),
		});

		onSave();
	};

	return (
		<Modal isOpen>
			<article className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>{`${isEditing ? 'Edit' : 'Create'} Custom Fields`}</h3>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Custom Field Name</label>
					{newCustomFields.map(customField => {
						return (
							<div key={`customFieldContainer-${customField.id}`} className={css(styleSheet.formControl)}>
								<div className={css(styleSheet.formControlInputWrap)}>
									<Input
										id={`customField-${customField.id}`}
										onChange={e => updateCustomField(customField, e.target.value)}
										styles={[styleSheet.fieldInput]}
										type='text'
										value={customField.label}
										placeholder='Add Custom Field'
									/>
									<button onClick={() => handleRemoveCustomField(customField)}>
										<XIcon color={charmGray} height={12} width={12} />
									</button>
								</div>
							</div>
						);
					})}
					{newCustomFields.length < MAX_CUSTOM_FIELDS ? (
						<button className={css(styleSheet.addMore)} onClick={handleAddMore}>
							<RoundAddIcon />
							Add another custom field
						</button>
					) : null}
				</div>
				<footer className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={save}>
						Save
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={cancel}>
						Cancel
					</button>
				</footer>
			</article>
		</Modal>
	);
};
